import { isNil } from 'lodash';
import type { ReactNode } from 'react';
import React, { useContext, useRef } from 'react';
import { ProductionInput } from 'sr-types/lib/production/v1/graphql';
import { updateProduction } from '../../../common/components/person/person';
import { CONSTANTS_ID } from '../../../common/constants/constants';
import { FormContainer } from '../../../common/form/FormContainer';
import { FormContext } from '../../../common/form/FormContext';
import FormItem from '../../../common/form/FormItem';
import FormPanel from '../../../common/form/FormPanel';
import FormWidget from '../../../common/form/FormWidget';
import { useL10n } from '../../../common/hooks/useL10n';
import { useMutationWithContext } from '../../../common/hooks/useMutationWithContext';
import I18n from '../../../common/i18n/I18n';
import { autoCreateQuery, client } from '../../../production/helpers/production';
import { toInput, useReadWriteGql } from '../../../production/helpers/productionApi';
import { removeOwnerOrganizationAndSetId } from '../../../production/helpers/productionUtils';
import { getOption, quickAddProduction } from '../../../production/helpers/QuickAddProduction';
import { ProductionOptionRenderer } from '../../../supply/booking/ProductionOptionRenderer';

export default function CreativeCreditsFormItems({
    disableTypeField,
    showReferenceId,
    footerAccessory
}: {
    disableTypeField: boolean;
    showReferenceId?: string;
    footerAccessory?: ReactNode;
}) {
    const { state, handleChange } = useContext(FormContext);
    const { data, saveMutation } = useReadWriteGql(state?.showReference?.id || CONSTANTS_ID.createId);
    const [save] = useMutationWithContext(autoCreateQuery, client);
    const { l10n } = useL10n();
    const initialState = useRef(state);

    const onTypeChange = (name: string, value: string) => {
        handleChange(name, value);
        const loadedProductionId = data?.production?.identity?.id;
        if (
            state &&
            state.showReference &&
            !state.showType &&
            data?.production &&
            loadedProductionId === state.showReference.id
        ) {
            const existingInput = removeOwnerOrganizationAndSetId(
                toInput(state.showReference.id, data.production, l10n, false)
            );
            const input: ProductionInput = {
                ...existingInput,
                productionType: value
            };
            updateProduction(saveMutation, input);
        }
    };

    return (
        <FormPanel>
            <FormContainer>
                <FormItem half>
                    {initialState?.current?.showName && isNil(initialState?.current?.showReference) ? (
                        <FormWidget name={`showName`} label={<I18n token="person.creative.credits.production" />} />
                    ) : (
                        <FormWidget
                            name="showReference"
                            label={<I18n token="person.creative.credits.production" />}
                            addOption
                            component="ReferenceAutocomplete"
                            entity="Production"
                            OptionRenderer={ProductionOptionRenderer}
                            onCreateHandle={quickAddProduction}
                            getOption={getOption}
                            saveMutation={save}
                            preload={false}
                            testId={'creativeCreditProduction'}
                        />
                    )}
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="showType"
                        label={<I18n token="person.creative.credits.show.type" />}
                        component="MetadataAutocomplete"
                        metadataName="ShowType"
                        value={state?.showType}
                        autocompleteKey={showReferenceId}
                        disabled={disableTypeField || (!state?.showReference && !state?.showName)}
                        onChange={(n: string, v: string) => onTypeChange(n, v)}
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="department"
                        label={<I18n token="person.creative.credits.department" />}
                        metadataName="Department"
                        allowCreate
                        component="MetadataAutocomplete"
                        testId={'creativeCreditDepartment'}
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="professionalRoles"
                        label={<I18n token="person.creative.credits.production.roles" />}
                        multiple
                        component="MetadataAutocomplete"
                        metadataName="Role"
                        testId={'creativeCreditRoles'}
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="location"
                        label={<I18n token="person.creative.credits.location" />}
                        component="Location"
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="keyCredit"
                        component="Switch"
                        label={<I18n token="person.creative.credits.key.credit" />}
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="dateRange.start"
                        label={<I18n token="person.creative.credits.start.date" />}
                        component="Date"
                    />
                </FormItem>
                <FormItem half>
                    <FormWidget
                        name="dateRange.end"
                        label={<I18n token="person.creative.credits.end.date" />}
                        component="Date"
                    />
                </FormItem>
                {footerAccessory ? <FormItem>{footerAccessory}</FormItem> : null}
            </FormContainer>
        </FormPanel>
    );
}
